import React, {useState} from 'react';
import {useTranslation} from 'react-i18next';
import {toast} from 'react-toastify';
import {Modal, ModalBody, ModalFooter, ModalHeader} from 'reactstrap';
import {AlertsApi} from '../../../api/alertsApi';
import {APIClient} from '../../../utils/APIClient';
import {API_URL} from '../../../settings/local_settings';
import {
    alertLineNames,
    coordinatesKey,
    joinLineNames,
    resnoozedLineNames,
    resnoozeTargetsOf,
    snoozeLanded,
    snoozeTargetsOf,
    uniqueCoordinates,
    unsnoozeLanded,
    unsnoozeTargetsOf,
} from '../../alerts_center/shared/alertLines';
import {
    ALERT_SNOOZE_PERIOD_FOREVER,
    ALERT_SNOOZE_PERIOD_ONE_WEEK,
    ALERT_SNOOZE_PERIOD_THIS_MONTH,
    ALERT_SNOOZE_PERIOD_TODAY,
} from '../../../periods';
import {snoozeEndsAt} from '../../dashboard/SnoozeAllAlertsModal';

/*
 * Silenciar y reactivar alertas vigentes: la lógica y el modal que comparten el popover del Listado
 * v2 (`AlertsPopover`) y la pantalla de Alertas del cliente (`ClientAlertsView`). Dos superficies
 * con su propio silenciado terminarían ofreciendo períodos distintos para la misma alerta.
 */

// Los cuatro períodos son los que ya acepta `AlertSnooze` en el backend; el default es una semana
// (el caso central: "lo estoy trabajando y no quiero el recordatorio").
const SNOOZE_PERIODS = [
    ALERT_SNOOZE_PERIOD_TODAY,
    ALERT_SNOOZE_PERIOD_ONE_WEEK,
    ALERT_SNOOZE_PERIOD_THIS_MONTH,
    ALERT_SNOOZE_PERIOD_FOREVER,
];
const DEFAULT_SNOOZE_PERIOD = ALERT_SNOOZE_PERIOD_ONE_WEEK;

// Cuántos pedidos de silenciar o reactivar viajan a la vez. «Silenciar estas N» de «Ahora» puede ser de
// toda la agencia: con `Promise.all` eran cientos de pedidos simultáneos (C-ac-fe-ahora-06).
export const SNOOZE_CONCURRENCY = 6;

/**
 * `Promise.all` de `tasks` (funciones que devuelven una promesa) con a lo sumo `limit` en vuelo. Los
 * resultados vuelven en el orden de `tasks`; si una rechaza, rechaza todo, como `Promise.all`.
 */
export function runPooled(tasks, limit = SNOOZE_CONCURRENCY) {
    const results = new Array(tasks.length);
    let next = 0;
    const worker = () => {
        if (next >= tasks.length) return Promise.resolve();
        const index = next;
        next += 1;
        return Promise.resolve()
            .then(() => tasks[index]())
            .then((value) => {
                results[index] = value;
                return worker();
            });
    };
    const workers = Array.from({length: Math.min(limit, tasks.length)}, worker);
    return Promise.all(workers).then(() => results);
}

/**
 * Estado del silenciado. `reload` vuelve a pedir la lista después de escribir: las acciones NO
 * tienen endpoints propios — cada ítem viaja con las coordenadas (`snooze`) de los endpoints de
 * silenciado que ya existen, así que "Silenciar todas" son N llamadas a esos (y NO
 * `alert-snooze-all`, que apaga TODOS los indicadores de una línea: otra cosa).
 */
export function useAlertSnooze({reload}) {
    const {t} = useTranslation();
    // `null` = modal cerrado. `{item}` silencia una; `{item: null, items}` silencia todas esas.
    const [snoozeTarget, setSnoozeTarget] = useState(null);
    const [period, setPeriod] = useState(DEFAULT_SNOOZE_PERIOD);
    const [isSaving, setIsSaving] = useState(false);

    const openSnooze = (item, allItems) => {
        setPeriod(DEFAULT_SNOOZE_PERIOD);
        setSnoozeTarget({item: item || null, items: item ? [item] : (allItems || [])});
    };

    const closeSnooze = () => setSnoozeTarget(null);

    // Una línea YA silenciada se vuelve a silenciar con el plazo elegido: primero se reactiva y, sólo si
    // eso salió, se silencia de nuevo. Si el DELETE falla, la línea conserva su silencio viejo y cuenta
    // como no silenciada (el aviso de parcial lo dice).
    const resnooze = (coords) => AlertsApi.unsnooze(coords)
        .then((response) => (unsnoozeLanded(response) ? AlertsApi.snooze(coords, period) : response));

    const confirmSnooze = () => {
        // Una alerta de varias líneas se silencia en TODAS (una llamada por línea): el usuario silencia
        // el problema, no una de sus copias. Las que ya estaban silenciadas pasan al plazo elegido (si no,
        // una silenciada «Por hoy» haría volver el problema mañana). Sin repetir coordenadas: dos POST
        // iguales duplican el silencio.
        const toResnooze = uniqueCoordinates(snoozeTarget.items.flatMap(resnoozeTargetsOf));
        const resnoozeKeys = new Set(toResnooze.map(coordinatesKey));
        const coordinates = uniqueCoordinates(snoozeTarget.items.flatMap(snoozeTargetsOf))
            .filter((coords) => !resnoozeKeys.has(coordinatesKey(coords)));
        setIsSaving(true);
        runPooled([
            ...coordinates.map((coords) => () => AlertsApi.snooze(coords, period)),
            ...toResnooze.map((coords) => () => resnooze(coords)),
        ])
            .then((responses) => {
                // Silenciar algo ya silenciado no es una falla del usuario (`snoozeLanded`). Si no
                // quedó NINGUNA, error; si quedaron algunas, se dice, porque la fila puede seguir
                // apareciendo como vigente en las líneas que fallaron. En «Silenciar todas» lo que
                // falla son alertas, no líneas (una URL no es de ninguna).
                const landed = responses.filter(snoozeLanded).length;
                if (!landed) {
                    toast.error(t('clients_v2.alerts_snooze_error'));
                } else if (landed < responses.length) {
                    toast.warning(t(snoozeTarget.item
                        ? 'clients_v2.alerts_snooze_partial'
                        : 'clients_v2.alerts_snooze_all_partial'));
                }
                closeSnooze();
                return reload();
            })
            .finally(() => setIsSaving(false));
    };

    const reactivate = (item) => {
        if (isSaving) return;   // dos clicks rapidos mandaban dos DELETE
        setIsSaving(true);
        runPooled(uniqueCoordinates(unsnoozeTargetsOf(item)).map((coords) => () => AlertsApi.unsnooze(coords)))
            .then((responses) => {
                // Reactivar algo que ya estaba activo (`ERROR_NOT_SNOOZED`) deja la línea como se pidió.
                const landed = responses.filter(unsnoozeLanded).length;
                if (!landed) {
                    toast.error(t('clients_v2.alerts_unsnooze_error'));
                    return null;
                }
                if (landed < responses.length) {
                    toast.warning(t('clients_v2.alerts_unsnooze_partial'));
                }
                return reload();
            })
            .finally(() => setIsSaving(false));
    };

    return {snoozeTarget, period, setPeriod, isSaving, openSnooze, closeSnooze, confirmSnooze, reactivate};
}

/**
 * Hasta cuándo callaría cada plazo, ANTES de elegirlo: «hasta las 23:59», «hasta el 15 de octubre», «sin
 * vencimiento». Mismo cálculo (`snoozeEndsAt`, espejo del backend) y mismos textos que el modal de la
 * campanita del Tablero, así las dos puertas de silenciar dicen lo mismo.
 */
export function snoozeUntilLabel(period, t, language, now = new Date()) {
    const endsAt = snoozeEndsAt(period, now);
    if (!endsAt) return t('dashboard.snooze_all.until_never');
    if (period === ALERT_SNOOZE_PERIOD_TODAY) {
        return t('dashboard.snooze_all.until_time', {
            time: endsAt.toLocaleTimeString(language || undefined, {hour: '2-digit', minute: '2-digit'}),
        });
    }
    return t('dashboard.snooze_all.until_date', {
        date: endsAt.toLocaleDateString(language || undefined, {day: 'numeric', month: 'long'}),
    });
}

/**
 * El modal de elegir por cuánto tiempo. Sólo se monta con un objetivo abierto.
 *
 * `clientName` nombra al cliente en «Silenciar todas» — hace falta en el listado, donde hay muchos.
 * En la pantalla de un solo cliente no se pasa y el título no lo nombra.
 */
export function AlertSnoozeModal({snooze, clientName}) {
    const {t, i18n} = useTranslation();
    const {snoozeTarget, period, setPeriod, isSaving, closeSnooze, confirmSnooze} = snooze;
    if (!snoozeTarget) return null;

    // Silenciar UNA alerta de varias líneas la silencia en todas: el modal lo dice antes de confirmar, y
    // nombra las que ya estaban silenciadas, porque pasan al plazo que se elija acá.
    const lineNames = snoozeTarget.item ? alertLineNames(snoozeTarget.item) : [];
    const resnoozedNames = snoozeTarget.item ? resnoozedLineNames(snoozeTarget.item) : [];

    const titleAll = clientName
        ? t('clients_v2.alert_snooze_modal_title_all',
            {n: snoozeTarget.items.length, client: clientName, interpolation: {escapeValue: false}})
        : t('client_alerts.snooze_modal_title_all', {count: snoozeTarget.items.length});

    return (
        <Modal isOpen toggle={closeSnooze}>
            <ModalHeader toggle={closeSnooze}>
                {snoozeTarget.item ? t('clients_v2.alert_snooze_modal_title_one') : titleAll}
            </ModalHeader>
            <ModalBody>
                <div className="cv2-snooze">
                    <p className="cv2-snooze__sub">{t('clients_v2.alert_snooze_modal_sub')}</p>
                    {lineNames.length > 1 && (
                        <p className="cv2-snooze__sub" data-testid="snooze-modal-lines">
                            {t('clients_v2.alert_snooze_modal_lines', {
                                names: joinLineNames(lineNames, i18n.language),
                                interpolation: {escapeValue: false},
                            })}
                        </p>
                    )}
                    {lineNames.length > 1 && resnoozedNames.length > 0 && (
                        <p className="cv2-snooze__sub" data-testid="snooze-modal-resnooze">
                            {t('clients_v2.alert_snooze_modal_resnooze', {
                                count: resnoozedNames.length,
                                names: joinLineNames(resnoozedNames, i18n.language),
                                interpolation: {escapeValue: false},
                            })}
                        </p>
                    )}
                    {SNOOZE_PERIODS.map((option) => (
                        <label
                            key={option}
                            className={`cv2-snooze__op${option === period ? ' -on' : ''}`}
                        >
                            <input type="radio" name="cv2-snooze-period" value={option}
                                   checked={option === period}
                                   onChange={() => setPeriod(option)}/>
                            <span className="cv2-snooze__content">
                                <span className="cv2-snooze__t">
                                    {t(`clients_v2.alert_snooze_period_${option}`)}
                                </span>
                                <span className="cv2-snooze__d">
                                    {t(`clients_v2.alert_snooze_period_desc_${option}`)}
                                </span>
                            </span>
                            <span className="cv2-snooze__until">{snoozeUntilLabel(option, t, i18n.language)}</span>
                        </label>
                    ))}
                    <p className="cv2-snooze__note">
                        <strong>{t('clients_v2.alert_snooze_note_title')}</strong>
                        {` ${t('clients_v2.alert_snooze_note')}`}
                    </p>
                </div>
            </ModalBody>
            <ModalFooter>
                <button type="button" className="btn -negative" onClick={closeSnooze}>
                    {t('clients_v2.alert_snooze_cancel')}
                </button>
                <button type="button" className="btn" onClick={confirmSnooze}
                        disabled={isSaving}>
                    {t('clients_v2.alert_snooze')}
                </button>
            </ModalFooter>
        </Modal>
    );
}

/**
 * Copia el prompt de una alerta. El prompt lo manda el BACKEND (`item.prompt`), donde vive
 * versionado y editable en caliente con `/edit-prompt`; si no viene, la acción NO se ofrece (el
 * backend omite la clave cuando la agencia no tiene el módulo MCP o el indicador no tiene prompt).
 *
 * Se copia la variante de cartera (`prompt_text_agency_wide`), el default de TODAS las superficies de
 * copiado (handoff de prompts del Backend): no sabemos si el GPT del usuario ve a todos o a uno, y
 * fijar el cliente por id también funciona en un GPT de un solo cliente (es redundante, no
 * incorrecto), mientras que al revés el agente tiene que resolver el nombre y puede tropezar con
 * homónimos.
 */
export function copyAlertPrompt(item, clientId, t) {
    const text = item.prompt.prompt_text_agency_wide || item.prompt.prompt_text;
    if (!navigator.clipboard) {
        toast.error(t('clients_v2.alert_prompt_copy_error'));
        return;
    }
    navigator.clipboard.writeText(text)
        .then(() => {
            toast.success(t('clients_v2.alert_prompt_copied'));
            // Ping de uso (fire-and-forget, patrón PromptLibraryCard): los prompts de alerta son de
            // la biblioteca oficial (familia alerta-*) — la clave del payload es `prompt_id`, no
            // `id`. Solo se registra si la copia ocurrió.
            if (item.prompt.prompt_id) {
                new APIClient(`${API_URL}agent/prompt-library/${item.prompt.prompt_id}/used/`)
                    .call('POST', {agency_client_id: clientId});
            }
        })
        .catch(() => toast.error(t('clients_v2.alert_prompt_copy_error')));
}
